/**
 * Mem0 Configuration Panel
 * Configure memory extraction prompts and categories (JSON format)
 * Matches the old admin interface exactly
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Loading } from '@/components/common';
import { Save, RotateCcw, AlertTriangle, BookOpen, ExternalLink, AlertCircle } from 'lucide-react';
import { useMem0Config, useUpdateMem0Config } from '../hooks/useSystemConfig';
import { useI18n } from '@/i18n';
import type { Mem0Config } from '@/types/system';

export const Mem0ConfigPanel: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.mem0;
  const { data: config, isLoading, error, refetch } = useMem0Config();
  const updateConfig = useUpdateMem0Config();

  const [userPrompt, setUserPrompt] = useState('');
  const [agentPrompt, setAgentPrompt] = useState('');
  const [updatePrompt, setUpdatePrompt] = useState('');
  const [userCategories, setUserCategories] = useState('');
  const [agentCategories, setAgentCategories] = useState('');

  // Format JSON for display
  const formatJson = (jsonStr: string | undefined): string => {
    if (!jsonStr) return '';
    try {
      const obj = JSON.parse(jsonStr);
      return JSON.stringify(obj, null, 2);
    } catch {
      return jsonStr;
    }
  };

  useEffect(() => {
    if (config) {
      setUserPrompt(config.user_memory_extraction_prompt || '');
      setAgentPrompt(config.agent_memory_extraction_prompt || '');
      setUpdatePrompt(config.memory_update_prompt || '');
      setUserCategories(formatJson(config.user_memory_categories));
      setAgentCategories(formatJson(config.agent_memory_categories));
    }
  }, [config]);

  // Validate categories JSON
  const validateCategories = (jsonStr: string, fieldName: string): string | null => {
    if (!jsonStr || !jsonStr.trim()) return null;
    try {
      const categories = JSON.parse(jsonStr);
      if (typeof categories !== 'object' || categories === null || Array.isArray(categories)) {
        return `${fieldName} must be a JSON object`;
      }
      for (const [catName, catConfig] of Object.entries(categories)) {
        const config = catConfig as { description?: string; temporal?: boolean };
        if (!config.description || typeof config.description !== 'string') {
          return `${fieldName}: Category '${catName}' must have a 'description' string field`;
        }
        if (typeof config.temporal !== 'boolean') {
          return `${fieldName}: Category '${catName}' must have a 'temporal' boolean field`;
        }
      }
      return null;
    } catch (e) {
      return `Invalid ${fieldName} JSON: ${(e as Error).message}`;
    }
  };

  const handleSave = async () => {
    // Validate prompts
    if (!userPrompt || userPrompt.trim().length < 50) {
      alert(t?.validation?.userPromptMin);
      return;
    }
    if (!agentPrompt || agentPrompt.trim().length < 50) {
      alert(t?.validation?.agentPromptMin);
      return;
    }
    if (!updatePrompt || updatePrompt.trim().length < 50) {
      alert(t?.validation?.updatePromptMin);
      return;
    }
    if (userPrompt.length > 8000) {
      alert(t?.validation?.userPromptMax);
      return;
    }
    if (agentPrompt.length > 8000) {
      alert(t?.validation?.agentPromptMax);
      return;
    }
    if (updatePrompt.length > 4000) {
      alert(t?.validation?.updatePromptMax);
      return;
    }

    // Validate categories
    const userCatError = validateCategories(userCategories, 'User memory categories');
    if (userCatError) {
      alert(userCatError);
      return;
    }
    const agentCatError = validateCategories(agentCategories, 'Agent memory categories');
    if (agentCatError) {
      alert(agentCatError);
      return;
    }

    try {
      const newConfig: Mem0Config = {
        user_memory_extraction_prompt: userPrompt.trim(),
        agent_memory_extraction_prompt: agentPrompt.trim(),
        memory_update_prompt: updatePrompt.trim(),
        user_memory_categories: userCategories.trim(),
        agent_memory_categories: agentCategories.trim(),
      };

      await updateConfig.mutateAsync(newConfig);
      alert(t?.saveSuccess);
    } catch (error) {
      alert(`${t?.saveFailed}: ${error}`);
    }
  };

  const handleReloadFromDatabase = () => {
    refetch();
  };

  // Reset individual fields to hardcoded defaults
  const handleResetPrompt = async (fieldName: 'user_memory_extraction_prompt' | 'agent_memory_extraction_prompt' | 'memory_update_prompt') => {
    const endpointMap = {
      'user_memory_extraction_prompt': '/v1/admin/system/mem0/user-memory-extraction-prompt',
      'agent_memory_extraction_prompt': '/v1/admin/system/mem0/agent-memory-extraction-prompt',
      'memory_update_prompt': '/v1/admin/system/mem0/memory-update-prompt',
    };

    try {
      const response = await fetch(endpointMap[fieldName], {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || ''}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const defaultPrompt = data.prompt || '';
        if (!defaultPrompt) {
          alert(t?.noDefaultPrompt);
          return;
        }

        if (fieldName === 'user_memory_extraction_prompt') {
          setUserPrompt(defaultPrompt);
        } else if (fieldName === 'agent_memory_extraction_prompt') {
          setAgentPrompt(defaultPrompt);
        } else {
          setUpdatePrompt(defaultPrompt);
        }
        alert(t?.resetSuccess);
      } else {
        throw new Error(`Failed to load default prompt: ${response.status}`);
      }
    } catch (error) {
      alert(`${t?.resetFailed}: ${(error as Error).message}`);
    }
  };

  const handleResetCategories = async (fieldName: 'user_memory_categories' | 'agent_memory_categories') => {
    const endpointMap = {
      'user_memory_categories': '/v1/admin/system/mem0/user-memory-categories',
      'agent_memory_categories': '/v1/admin/system/mem0/agent-memory-categories',
    };

    try {
      const response = await fetch(endpointMap[fieldName], {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || ''}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const defaultCategories = data.categories || '';
        if (!defaultCategories) {
          alert(t?.noDefaultCategories);
          return;
        }

        // Pretty-print the JSON
        const formatted = formatJson(defaultCategories);

        if (fieldName === 'user_memory_categories') {
          setUserCategories(formatted);
        } else {
          setAgentCategories(formatted);
        }
        alert(t?.resetSuccess);
      } else {
        throw new Error(`Failed to load default categories: ${response.status}`);
      }
    } catch (error) {
      alert(`${t?.resetFailed}: ${(error as Error).message}`);
    }
  };

  if (isLoading) {
    return <Loading message={t?.loading} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{t?.loadFailed}: {error.message}</span>
      </div>
    );
  }

  const hasChanges =
    userPrompt !== (config?.user_memory_extraction_prompt || '') ||
    agentPrompt !== (config?.agent_memory_extraction_prompt || '') ||
    updatePrompt !== (config?.memory_update_prompt || '') ||
    userCategories !== formatJson(config?.user_memory_categories) ||
    agentCategories !== formatJson(config?.agent_memory_categories);

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              {t?.warningBanner}
            </p>
          </div>
        </div>
      </div>

      {/* Help Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <BookOpen className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-900 mb-2">{t?.helpTitle}</h3>
            <p className="text-sm text-blue-700 mb-2">
              {t?.helpDescription}
            </p>
            <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
              {t?.helpItems?.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <div className="mt-3">
              <a
                href="https://docs.mem0.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center text-sm"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                {t?.documentation}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{t?.title}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {t?.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleReloadFromDatabase} disabled={updateConfig.isPending}>
            <RotateCcw className="w-4 h-4 mr-2" />
            {t?.reloadFromDatabase}
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || updateConfig.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {updateConfig.isPending ? t?.saving : t?.saveSystemDefaults}
          </Button>
        </div>
      </div>

      {/* User Memory Extraction Prompt */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700 flex items-center">
            <span className="w-4 h-4 mr-2 text-blue-500">👤</span>
            {t?.userMemoryPrompt}
          </label>
          <button
            onClick={() => handleResetPrompt('user_memory_extraction_prompt')}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t?.resetToHardcoded}
          </button>
        </div>
        <Textarea
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          rows={8}
          className="font-mono text-sm"
        />
        <p className="mt-2 text-xs text-gray-500">
          {t?.promptMinMax}
        </p>
      </Card>

      {/* Agent Memory Extraction Prompt */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700 flex items-center">
            <span className="w-4 h-4 mr-2 text-purple-500">🤖</span>
            {t?.agentMemoryPrompt}
          </label>
          <button
            onClick={() => handleResetPrompt('agent_memory_extraction_prompt')}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t?.resetToHardcoded}
          </button>
        </div>
        <Textarea
          value={agentPrompt}
          onChange={(e) => setAgentPrompt(e.target.value)}
          rows={8}
          className="font-mono text-sm"
        />
        <p className="mt-2 text-xs text-gray-500">
          {t?.promptMinMax}
        </p>
      </Card>

      {/* Memory Update Prompt */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700 flex items-center">
            <span className="w-4 h-4 mr-2 text-green-500">🔄</span>
            {t?.memoryUpdatePrompt}
          </label>
          <button
            onClick={() => handleResetPrompt('memory_update_prompt')}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t?.resetToHardcoded}
          </button>
        </div>
        <Textarea
          value={updatePrompt}
          onChange={(e) => setUpdatePrompt(e.target.value)}
          rows={8}
          className="font-mono text-sm"
        />
        <p className="mt-2 text-xs text-gray-500">
          {t?.promptUpdateMinMax}
        </p>
      </Card>

      {/* User Memory Categories (JSON) */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700 flex items-center">
            <span className="w-4 h-4 mr-2 text-blue-500">🏷️</span>
            {t?.userMemoryCategories}
          </label>
          <button
            onClick={() => handleResetCategories('user_memory_categories')}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t?.resetToHardcoded}
          </button>
        </div>
        <Textarea
          value={userCategories}
          onChange={(e) => setUserCategories(e.target.value)}
          rows={12}
          placeholder='{"preference": {"description": "用户偏好", "temporal": false}}'
          className="font-mono text-xs bg-gray-50"
          spellCheck={false}
        />
        <p className="mt-2 text-xs text-gray-500">
          {t?.categoriesHint}
        </p>
      </Card>

      {/* Agent Memory Categories (JSON) */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700 flex items-center">
            <span className="w-4 h-4 mr-2 text-purple-500">🏷️</span>
            {t?.agentMemoryCategories}
          </label>
          <button
            onClick={() => handleResetCategories('agent_memory_categories')}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            {t?.resetToHardcoded}
          </button>
        </div>
        <Textarea
          value={agentCategories}
          onChange={(e) => setAgentCategories(e.target.value)}
          rows={12}
          placeholder='{"analysis": {"description": "分析结果", "temporal": false}}'
          className="font-mono text-xs bg-gray-50"
          spellCheck={false}
        />
        <p className="mt-2 text-xs text-gray-500">
          {t?.categoriesHint}
        </p>
      </Card>

      {/* Metadata */}
      {config?.version !== undefined && (
        <div className="text-sm text-gray-500">
          <p>
            {t?.version}: <span>{config.version}</span> |
            {t?.lastUpdatedBy}: <span>{config.updated_by || 'N/A'}</span> |
            <span> {config.updated_at ? new Date(config.updated_at).toLocaleString() : 'N/A'}</span>
          </p>
        </div>
      )}
    </div>
  );
};
